import React, { useEffect, useRef, useState } from 'react';
import { X, MapPin, AlertTriangle, AlertCircle, Loader, Hash, Package } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { LocationItem } from '../../../../../services/inventoryService';

/**
 * TW 36413029: avslutning av en lokasjon i fritelling med «Vis varer på lokasjon».
 * Varene som Compass har på lokasjonen, men som ikke er talt, gås gjennom én og én med
 * valgene «Tell til 0», «Tell» og «La stå». Det finnes bevisst ingen «null alle».
 * Dialogen blokkerer ikke — «Avslutt» går videre og lar resten stå urørt.
 */
interface LocationZeroCountDialogProps {
  isOpen: boolean;
  locationName: string;
  /** Øyeblikksbilde av utalte varer da dialogen ble åpnet. */
  items: LocationItem[];
  /** Lagrer 0-linje(r) for varen. Kaster ved feil. */
  onZero: (item: LocationItem) => Promise<void>;
  /** Lukker dialogen og lar operatøren telle varen. */
  onCount: (item: LocationItem) => void;
  /** Gjennomgangen er ferdig eller avsluttet — gå videre. */
  onFinish: () => void;
}

export const LocationZeroCountDialog: React.FC<LocationZeroCountDialogProps> = ({
  isOpen,
  locationName,
  items,
  onZero,
  onCount,
  onFinish
}) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Hindrer at onFinish kalles to ganger for samme gjennomgang
  const finishedRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setIndex(0);
      setSaving(false);
      setError(null);
      finishedRef.current = false;
    }
  }, [isOpen, items]);

  const finish = () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    onFinish();
  };

  // Alle varer er behandlet → gå videre
  useEffect(() => {
    if (isOpen && items.length > 0 && index >= items.length) {
      finish();
    }
  }, [isOpen, index, items.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen || index >= items.length) return null;

  const item = items[index];
  const tracked = item.kind !== 'standard';
  const trackedCount = item.rows.length;

  const next = () => {
    setError(null);
    setIndex(i => i + 1);
  };

  const handleZero = async () => {
    setSaving(true);
    setError(null);
    try {
      await onZero(item);
      next();
    } catch (err) {
      console.error('Feil ved telling til 0:', err);
      setError(t('warehouse.modules.inventory.locationItems.zeroFailed', {}, 'Kunne ikke lagre telling til 0. Prøv igjen.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end tablet:items-center justify-center z-50 p-0 tablet:p-4">
      <div className="bg-white rounded-t-2xl tablet:rounded-lg max-w-md w-full animate-slide-up tablet:animate-none">
        <div className="p-4 border-b border-gray-200 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-base sm:text-lg font-semibold flex items-center">
              <MapPin className="h-5 w-5 mr-2 flex-shrink-0 text-purple-500" />
              <span className="break-words">
                {t('warehouse.modules.inventory.locationItems.dialogTitle', { '0': items.length, '1': locationName }, '{0} varer er ikke talt på {1}')}
              </span>
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              {t('warehouse.modules.inventory.locationItems.dialogProgress', { '0': index + 1, '1': items.length }, 'Vare {0} av {1}')}
            </p>
          </div>
          <button
            onClick={finish}
            disabled={saving}
            className="text-gray-500 hover:text-gray-700 p-2 min-h-[44px] min-w-[44px] flex items-center justify-center -mr-2 flex-shrink-0"
            aria-label={t('common.buttons.close')}
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-3">
          <div className="p-3 border rounded-lg bg-gray-50 border-gray-200">
            <div className="font-mono text-sm font-semibold break-all">{item.itemNo}</div>
            <div className="text-sm text-gray-600 break-words">{item.description}</div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-sm text-gray-700">
              <span>
                {t('warehouse.modules.inventory.locationItems.balance', { '0': item.quantity }, 'Saldo: {0}')}
              </span>
              {item.kind === 'batch' && (
                <span className="inline-flex items-center text-orange-600">
                  <Package className="h-3.5 w-3.5 mr-1" />
                  {t('warehouse.modules.inventory.locationItems.batchCount', { '0': trackedCount }, 'Batch · {0} batcher')}
                </span>
              )}
              {item.kind === 'serial' && (
                <span className="inline-flex items-center text-purple-600">
                  <Hash className="h-3.5 w-3.5 mr-1" />
                  {t('warehouse.modules.inventory.locationItems.serialCount', { '0': trackedCount }, 'Serie · {0} serienr')}
                </span>
              )}
            </div>
            {tracked && (
              <p className="text-xs text-gray-500 mt-2">
                {item.kind === 'batch'
                  ? t('warehouse.modules.inventory.locationItems.zeroAllBatchesHint', {}, '«Tell til 0» registrerer alle batchene som mangler.')
                  : t('warehouse.modules.inventory.locationItems.zeroAllSerialsHint', {}, '«Tell til 0» registrerer alle serienumrene som mangler.')}
              </p>
            )}
          </div>

          {item.reservedQuantity > 0 && (
            <div className="p-2 rounded flex items-start text-xs sm:text-sm bg-amber-50 text-amber-800">
              <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
              <span className="break-words">
                {t('warehouse.modules.inventory.locationItems.reservedWarning', { '0': item.reservedQuantity }, '{0} er reservert på lokasjonen. Telles varen til 0, kan plukk av reservasjonen feile.')}
              </span>
            </div>
          )}

          {error && (
            <div className="p-2 rounded flex items-start text-xs sm:text-sm bg-red-50 text-red-800">
              <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
              <span className="break-words">{error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 gap-2">
            <button
              onClick={handleZero}
              disabled={saving}
              className="w-full px-4 py-3 bg-red-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-red-700 transition-colors disabled:opacity-50 min-h-[44px]"
            >
              {saving && <Loader className="animate-spin h-4 w-4" />}
              <span>{t('warehouse.modules.inventory.locationItems.countToZero', {}, 'Tell til 0')}</span>
            </button>
            <button
              onClick={() => onCount(item)}
              disabled={saving}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 min-h-[44px]"
            >
              {t('warehouse.modules.inventory.locationItems.count', {}, 'Tell')}
            </button>
            <button
              onClick={next}
              disabled={saving}
              className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 min-h-[44px]"
            >
              {t('warehouse.modules.inventory.locationItems.leave', {}, 'La stå')}
            </button>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200">
          <button
            onClick={finish}
            disabled={saving}
            className="w-full px-4 py-2.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 min-h-[44px] disabled:opacity-50"
          >
            {t('warehouse.modules.inventory.locationItems.finishDialog', {}, 'Avslutt — la resten stå')}
          </button>
        </div>
      </div>
    </div>
  );
};
